<template>
  <v-data-table
    class="styled-table contact-merge-table"
    hide-default-footer
    :loading="loading"
    :headers="headers"
    :items="tableItems"
    :mobile-breakpoint="0"
    fixed-header
  >
    <template v-slot:item.result="{ item }">
      <contacts-table-cell
        :field="item.key"
        :fieldValue="item.result"
        :contactName="contactName"
        :fieldSchema="getFieldSchema(item.key)"
      />
    </template>
  </v-data-table>
</template>

<script>
import { mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';

import ContactsTableCell from '@/views/contacts/components/ContactsTableCell.vue';
import { format, formatDistance } from 'date-fns';

export default {
  name: 'ContactMergeTable',
  components: { ContactsTableCell },
  props: {
    keptContact: {
      type: Object,
      default: () => ({}),
    },
    mergeContact: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      headers: [
        {
          text: 'Field',
          value: 'field',
          width: 200,
        },
        {
          text: 'Merge Preview',
          value: 'result',
          sortable: false,
        },
      ],
    };
  },
  computed: {
    ...mapGetters('teams', ['currentTeam']),
    ...mapGetters('fields', ['getSingleField']),

    contactName() {
      const firstName =
        this.keptContact?.first_name || this.mergeContact?.first_name;
      const lastName =
        this.keptContact?.last_name || this.mergeContact?.last_name;
      return `${firstName || ''} ${lastName || ''}`.trim() || 'Unknown';
    },

    fieldsVisibleForTeam() {
      return this.currentTeam?.contact_fields || [];
    },

    tableItems() {
      if (!this.keptContact || !this.mergeContact) {
        return [];
      }

      const keptFields = this.getContactFields(this.keptContact);
      const mergeFields = this.getContactFields(this.mergeContact);

      let tableItems = [];

      for (let key in keptFields) {
        const keptField = keptFields[key];
        const mergeField = mergeFields[key];

        if (
          !this.isFieldHidden(keptField) &&
          (!isEmpty(keptField?.value) || !isEmpty(mergeField?.value))
        ) {
          const value = this.getMergedValue(
            keptField?.value,
            mergeField?.value
          );

          tableItems.push({
            field: this.getFieldLabel(keptField),
            result: this.formatValue(value, key),
            key: key,
          });
        }
      }

      return tableItems;
    },
  },

  methods: {
    getFieldSchema(field) {
      return this.getSingleField(field);
    },

    getFieldType(field) {
      const fieldSchema = this.getSingleField(field);
      return fieldSchema?.type;
    },

    getFieldLabel(field) {
      let label = field.label === 'name' ? field.name : field.label;
      return label.replace(/_/g, ' ');
    },

    getMergedValue(keptValue, mergeValue, field) {
      if (isEmpty(keptValue) && isEmpty(mergeValue)) {
        return '';
      }

      if (isEmpty(keptValue)) {
        return mergeValue;
      }

      if (isEmpty(mergeValue)) {
        return keptValue;
      }

      if (Array.isArray(keptValue) && Array.isArray(mergeValue)) {
        return [...new Set([...keptValue, ...mergeValue])];
      }

      if (typeof keptValue === 'object' && typeof mergeValue === 'object') {
        return { ...keptValue, ...mergeValue };
      }

      return `${keptValue}`;
    },

    formatValue(value, field) {
      if (this.getFieldType(field) === 'time') {
        return this.formatDate(value, field);
      }

      if (['points', 'maxPoints'].includes(field)) {
        return (value || 0).toLocaleString('en-US');
      }

      if (['progress', 'grade'].includes(field)) {
        const isNumeric = typeof value === 'number';
        return isNumeric ? `${Math.round(value * 100)}%` : '';
      }

      return value;
    },

    formatDate(timestamp, field) {
      if (!timestamp) {
        return '';
      }

      if (typeof timestamp === 'number') {
        timestamp = timestamp * 1000;
      }

      const date = new Date(timestamp);

      if (field === 'Last Seen') {
        return formatDistance(date, new Date(), {
          addSuffix: true,
        });
      }

      const formatted = format(date, 'MMM dd yyyy, hh:mm a');
      const tz = date
        .toLocaleTimeString('en-us', { timeZoneName: 'short' })
        .split(' ')[2];

      return `${formatted} ${tz}`;
    },

    getContactFields(contactObject) {
      if (!contactObject) {
        return {};
      }

      const fieldsBucket = Object.values(contactObject).find(
        (item) =>
          typeof item === 'object' &&
          item !== null &&
          item.hasOwnProperty('fields') &&
          item.type === 'default'
      );

      return fieldsBucket?.fields || {};
    },

    isFieldHidden(field) {
      const fieldName = field.name;
      const hiddenFields = ['campaigns'];

      // "Show in Profile" field param
      if (field.active_profile === false) {
        return true;
      }

      if (field.type === 'secret') {
        return true;
      }

      if (hiddenFields.includes(fieldName)) {
        return true;
      }

      // field is not visible for the current team
      if (this.fieldsVisibleForTeam.length) {
        return !this.fieldsVisibleForTeam.includes(fieldName);
      }

      return false;
    },
  },
};
</script>

<style lang="less">
.contact-merge-table {
  box-shadow: @base-shadow;

  td.text-start:first-child {
    text-transform: capitalize;
  }

  td,
  th {
    &:first-child {
      border-right: 1px solid #eee;
    }
  }

  tr:hover {
    td:first-child {
      border-right: 1px solid #ccc;
    }
  }
}
</style>
